import React from "react";
import { motion } from "framer-motion";
import {
  SiExpress,
  SiGit,
  SiHtml5,
  SiLinux,
  SiMongodb,
  SiMysql,
  SiNodedotjs,
  SiReact,
  SiTailwindcss,
  SiTypescript,
  SiVercel,
  SiVscodium,
} from "react-icons/si";
import { useUiLabels } from "../context/LocaleContext";

const easeFluid = [0.32, 0.72, 0, 1] as const;

const iconByName: Record<string, React.ComponentType<{ className?: string }>> = {
  React: SiReact,
  TypeScript: SiTypescript,
  "Tailwind CSS": SiTailwindcss,
  HTML5: SiHtml5,
  "Node.js": SiNodedotjs,
  Express: SiExpress,
  MongoDB: SiMongodb,
  MySQL: SiMysql,
  Git: SiGit,
  "VS Code": SiVscodium,
  Linux: SiLinux,
  Vercel: SiVercel,
};

const SkillsSection = () => {
  const ui = useUiLabels();
  const { badge, title, intro, categories, closing, closingCode } = ui.skills;

  const skillCategories = categories.map((cat) => ({
    title: cat.title,
    skills: cat.skills.map((s) => ({
      name: s.name,
      icon: iconByName[s.name] ?? SiReact,
      color:
        s.name === "React"
          ? "text-sky-300"
          : s.name === "TypeScript"
            ? "text-blue-300"
            : s.name === "Tailwind CSS"
              ? "text-teal-300"
              : s.name === "HTML5"
                ? "text-orange-300"
                : s.name === "Node.js"
                  ? "text-emerald-300"
                  : s.name === "Express"
                    ? "text-zinc-300"
                    : s.name === "MongoDB"
                      ? "text-green-400"
                      : s.name === "MySQL"
                        ? "text-sky-200"
                        : s.name === "Git"
                          ? "text-rose-300"
                          : s.name === "VS Code"
                            ? "text-violet-300"
                            : s.name === "Linux"
                              ? "text-amber-200"
                              : "text-zinc-200",
    })),
  }));

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: easeFluid }}
      className="flex w-full min-h-0 items-start justify-center px-4 py-24 md:px-8 md:py-32"
    >
      <div className="w-full max-w-6xl select-none pb-16">
        <header className="mb-14 md:mb-20 max-w-2xl">
          <p className="mb-4 inline-flex rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-violet-200/90 ring-1 ring-violet-400/25">
            {badge}
          </p>
          <h1 className="text-4xl font-extrabold tracking-tight text-white md:text-5xl lg:text-6xl">{title}</h1>
          <p className="mt-5 text-lg text-zinc-400">{intro}</p>
        </header>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-12 md:gap-6">
          {skillCategories.map((category, idx) => (
            <motion.div
              key={category.title}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.8, delay: idx * 0.06, ease: easeFluid }}
              className={`bezel-outer md:col-span-4 ${idx === 0 ? "md:row-span-1" : ""}`}
            >
              <div className="bezel-inner p-6 md:p-7">
                <h2 className="mb-6 text-center text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-200/85">
                  {category.title}
                </h2>
                <div className="grid grid-cols-2 gap-3">
                  {category.skills.map((skill) => {
                    const Icon = skill.icon;
                    return (
                      <div
                        key={skill.name}
                        className="group flex flex-col items-center justify-center rounded-2xl bg-white/[0.03] px-3 py-4 ring-1 ring-white/[0.06] transition-[transform,background-color] duration-700 ease-fluid hover:bg-white/[0.06] active:scale-[0.98]"
                      >
                        <Icon className={`mb-2 h-8 w-8 ${skill.color} opacity-90 transition-transform duration-700 ease-fluid group-hover:scale-105`} />
                        <p className="text-center text-xs font-medium text-zinc-300">{skill.name}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          ))}

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.85, delay: 0.12, ease: easeFluid }}
            className="bezel-outer md:col-span-12"
          >
            <div className="bezel-inner flex flex-col gap-4 p-6 md:flex-row md:items-center md:justify-between md:p-8">
              <p className="max-w-xl text-sm leading-relaxed text-zinc-400">{closing}</p>
              <div className="shrink-0 rounded-2xl px-4 py-3 font-mono text-[11px] text-zinc-500 ring-1 ring-white/[0.08] bg-[#0a0a0c]">
                {closingCode}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default SkillsSection;
